// User delegation SAS for a single blob, signed in the browser with Web
// Crypto. Deliberately SDK-free: `generateBlobSASQueryParameters` in
// `@azure/storage-blob` is Node-only, and this repo talks to the REST API
// directly everywhere else anyway.
//
// Reference (string-to-sign layout, parameter names):
// https://learn.microsoft.com/rest/api/storageservices/create-user-delegation-sas
//
// This module is pure — no config, no fetch — so it can be exercised outside
// the browser bundle. The REST call that obtains the key lives in
// `~lib/storage/temporary-link`.

// The SAS `sv` parameter. Pinned on purpose: Azure picks the string-to-sign
// layout from this version, and `buildStringToSign` below implements the
// 24-field layout that versions 2020-12-06 through 2025-07-04 share
// (2025-07-05 inserts delegated-user fields). Move the two together or the
// signature stops validating.
export const SAS_VERSION = '2024-08-04';

export interface UserDelegationKey {
  // Object ID of the user the key was issued to. Every request made with a
  // SAS signed by this key is authorised as — and logged against — that user.
  signedOid: string;
  signedTid: string;
  // ISO 8601 strings kept verbatim from the response: they're echoed into
  // both the query string and the string-to-sign and must match byte for byte.
  signedStart: string;
  signedExpiry: string;
  // Always 'b' (blob) for keys issued by the Blob service.
  signedService: string;
  // REST version the key was requested with.
  signedVersion: string;
  // Base64-encoded HMAC key.
  value: string;
}

export interface BlobReadSasInput {
  // e.g. `https://stsendatasetcatalog.blob.core.windows.net`
  blobEndpoint: string;
  account: string;
  container: string;
  // Raw blob name, not percent-encoded (`foo/foo.zip`).
  blobName: string;
  key: UserDelegationKey;
  // Must not exceed the key's `signedExpiry`.
  expiresOn: Date;
}

// Builds a read-only, HTTPS-only URL for exactly one blob, valid from the
// key's start until `expiresOn`.
export async function buildBlobReadSasUrl(input: BlobReadSasInput): Promise<string> {
  const { key } = input;
  const se = toIsoSeconds(input.expiresOn);
  const signature = await hmacSha256Base64(key.value, buildStringToSign(input, se));

  const params = new URLSearchParams({
    sv: SAS_VERSION,
    spr: 'https',
    se,
    sr: 'b',
    sp: 'r',
    skoid: key.signedOid,
    sktid: key.signedTid,
    skt: key.signedStart,
    ske: key.signedExpiry,
    sks: key.signedService,
    skv: key.signedVersion,
    sig: signature,
  });

  return `${input.blobEndpoint}/${input.container}/${encodeBlobPath(input.blobName)}?${params.toString()}`;
}

// Field order is the contract. Every slot maps to a query parameter (or a
// response-header override) and is listed even when we leave it empty.
function buildStringToSign(input: BlobReadSasInput, se: string): string {
  const { key } = input;
  return [
    'r', // sp — signedPermissions
    '', // st — signedStart (omitted: valid on receipt, bounded by skt)
    se, // se — signedExpiry
    `/blob/${input.account}/${input.container}/${input.blobName}`, // canonicalizedResource (raw name)
    key.signedOid, // skoid
    key.signedTid, // sktid
    key.signedStart, // skt
    key.signedExpiry, // ske
    key.signedService, // sks
    key.signedVersion, // skv
    '', // saoid — signedAuthorizedUserObjectId (hierarchical namespace only)
    '', // suoid — signedUnauthorizedUserObjectId (hierarchical namespace only)
    '', // scid — signedCorrelationId
    '', // sip — signedIP
    'https', // spr — signedProtocol
    SAS_VERSION, // sv — signedVersion
    'b', // sr — signedResource
    '', // signedSnapshotTime
    '', // ses — signedEncryptionScope
    '', // rscc — Cache-Control override
    '', // rscd — Content-Disposition override
    '', // rsce — Content-Encoding override
    '', // rscl — Content-Language override
    '', // rsct — Content-Type override
  ].join('\n');
}

// Parses the `Get User Delegation Key` response body.
export function parseUserDelegationKeyXml(xml: string): UserDelegationKey {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.querySelector('parsererror')) {
    throw new Error('User delegation key response was not valid XML.');
  }
  const read = (tag: string): string => {
    const text = doc.querySelector(`UserDelegationKey > ${tag}`)?.textContent ?? '';
    if (!text) {
      throw new Error(`User delegation key response is missing <${tag}>.`);
    }
    return text;
  };
  return {
    signedOid: read('SignedOid'),
    signedTid: read('SignedTid'),
    signedStart: read('SignedStart'),
    signedExpiry: read('SignedExpiry'),
    signedService: read('SignedService'),
    signedVersion: read('SignedVersion'),
    value: read('Value'),
  };
}

// ISO 8601 UTC to the second (`2026-09-24T14:32:00Z`). `toISOString()` adds
// milliseconds, which none of Azure's documented SAS timestamp forms carry.
export function toIsoSeconds(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

async function hmacSha256Base64(keyBase64: string, message: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      'Web Crypto is unavailable. Temporary links need a secure context: https://, localhost or 127.0.0.1.',
    );
  }
  const cryptoKey = await subtle.importKey(
    'raw',
    base64ToBytes(keyBase64),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await subtle.sign(
    'HMAC',
    cryptoKey,
    new TextEncoder().encode(message),
  );
  return bytesToBase64(new Uint8Array(signature));
}

function base64ToBytes(base64: string) {
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

// Path segments can contain characters that need URL-encoding (spaces, `+`,
// `#`, …) but the `/` separators must stay literal.
function encodeBlobPath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}
